import { create } from 'zustand';
import { http } from '@/libs/http';
import { toast } from 'sonner';
import type {
    ServicePrincipal,
    ServicePrincipalCreate,
    ServicePrincipalUpdate,
    SpnSecret,
    SpnSecretCreate,
    SpnSecretCreated,
} from '@/types';

interface ServicePrincipalState {
    servicePrincipals: ServicePrincipal[];
    isLoading: boolean;
    error: string | null;

    list: (applicationId?: string) => Promise<ServicePrincipal[]>;
    get: (id: string) => Promise<ServicePrincipal>;
    create: (data: ServicePrincipalCreate) => Promise<ServicePrincipal>;
    update: (id: string, data: ServicePrincipalUpdate) => Promise<ServicePrincipal>;
    delete: (id: string) => Promise<void>;
    listSecrets: (spnId: string) => Promise<SpnSecret[]>;
    createSecret: (spnId: string, data: SpnSecretCreate) => Promise<SpnSecretCreated>;
    deleteSecret: (spnId: string, keyId: string) => Promise<void>;
}

export const useServicePrincipals = create<ServicePrincipalState>((set, get) => ({
    servicePrincipals: [],
    isLoading: false,
    error: null,

    list: async (applicationId?: string) => {
        set({ isLoading: true, error: null });
        try {
            const response = await http.get<any>('/mgmt/v1/spns');
            let data = Array.isArray(response) ? response : (response.items || []);
            if (applicationId) {
                data = data.filter((s: any) => s.application_id === applicationId);
            }
            set({ servicePrincipals: data, isLoading: false });
            return data;
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to load service principals';
            set({ error: errorMsg, isLoading: false });
            toast.error(errorMsg);
            return [];
        }
    },

    get: async (id) => {
        const found = get().servicePrincipals.find((spn) => spn.id === id);
        if (found) return found;
        const data = await http.get<ServicePrincipal>(`/mgmt/v1/spns/${id}`);
        set({ servicePrincipals: [...get().servicePrincipals.filter((s) => s.id !== id), data] });
        return data;
    },

    create: async (data) => {
        try {
            const response = await http.post<any>('/mgmt/v1/spns', data);
            const result = response.data as ServicePrincipal;
            set({ servicePrincipals: [...get().servicePrincipals, result] });
            toast.success('Service principal created successfully');
            return result;
        } catch (err: any) {
            toast.error(err.message || 'Failed to create service principal');
            throw err;
        }
    },

    update: async (id, data) => {
        try {
            const response = await http.put<any>(`/mgmt/v1/spns/${id}`, data);
            const result = response.data as ServicePrincipal;
            set({ servicePrincipals: get().servicePrincipals.map((s) => (s.id === id ? result : s)) });
            toast.success('Service principal updated successfully');
            return result;
        } catch (err: any) {
            toast.error(err.message || 'Failed to update service principal');
            throw err;
        }
    },

    delete: async (id) => {
        const previous = get().servicePrincipals;
        set({ servicePrincipals: previous.filter((s) => s.id !== id) });
        try {
            await http.delete(`/mgmt/v1/spns/${id}`);
            toast.success('Service principal deleted successfully');
        } catch (err: any) {
            set({ servicePrincipals: previous });
            toast.error(err.message || 'Failed to delete service principal');
            throw err;
        }
    },

    listSecrets: async (spnId) => {
        try {
            const response = await http.get<SpnSecret[]>(`/mgmt/v1/spns/${spnId}/secrets`);
            return response;
        } catch (err: any) {
            toast.error(err.message || 'Failed to load secrets');
            return [];
        }
    },

    createSecret: async (spnId, data) => {
        try {
            const response = await http.post<SpnSecretCreated>(`/mgmt/v1/spns/${spnId}/secrets`, data);
            toast.success('Secret created successfully');
            return response;
        } catch (err: any) {
            toast.error(err.message || 'Failed to create secret');
            throw err;
        }
    },

    deleteSecret: async (spnId, keyId) => {
        try {
            await http.delete(`/mgmt/v1/spns/${spnId}/secrets/${keyId}`);
            toast.success('Secret deleted successfully');
        } catch (err: any) {
            toast.error(err.message || 'Failed to delete secret');
            throw err;
        }
    },
}));

export const useServicePrincipal = (id?: string) => {
    const servicePrincipal = useServicePrincipals((state) =>
        id ? state.servicePrincipals.find((item) => item.id === id) ?? null : null,
    );

    const ensureId = (): string => {
        if (!id) throw new Error('Service Principal id is required.');
        return id;
    };

    return {
        servicePrincipal,
        get: () => useServicePrincipals.getState().get(ensureId()),
        update: (data: ServicePrincipalUpdate) => useServicePrincipals.getState().update(ensureId(), data),
        delete: () => useServicePrincipals.getState().delete(ensureId()),
        listSecrets: () => useServicePrincipals.getState().listSecrets(ensureId()),
        createSecret: (data: SpnSecretCreate) => useServicePrincipals.getState().createSecret(ensureId(), data),
        deleteSecret: (keyId: string) => useServicePrincipals.getState().deleteSecret(ensureId(), keyId),
    };
};
